import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Layers, FolderClock, CheckCircle2, CalendarClock, Trash2,
  UserX, UserMinus, ArchiveRestore, ShieldAlert, RefreshCw,
  ChevronRight, History, ArrowRight
} from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import SEO from "@/components/SEO";

const SECTIONS = [
  { id: "scope", title: "1. Scope", icon: Layers },
  { id: "why-retain", title: "2. Why We Retain Information", icon: FolderClock },
  { id: "principles", title: "3. Retention Principles", icon: CheckCircle2 },
  { id: "categories", title: "4. Indicative Retention Categories", icon: CalendarClock },
  { id: "deletion", title: "5. Deletion", icon: Trash2 },
  { id: "account-closure", title: "6. Account Closure", icon: UserX },
  { id: "client-requests", title: "7. Client Deletion Requests", icon: UserMinus },
  { id: "backups", title: "8. Backup Copies", icon: ArchiveRestore },
  { id: "secure-disposal", title: "9. Secure Disposal", icon: ShieldAlert },
  { id: "policy-review", title: "10. Policy Review", icon: RefreshCw },
];

const RETENTION_CATEGORIES = [
  {
    category: "Website enquiries",
    retention: "Up to 24 months after last meaningful interaction",
    note: "General contact inquiries and consultations",
  },
  {
    category: "CRM lead records",
    retention: "During relationship + reasonable historical period",
    note: "Prospective client communication and follow-ups",
  },
  {
    category: "Client service records",
    retention: "During engagement + applicable record-keeping period",
    note: "Statutory filing receipts, incorporation docs, registrations",
  },
  {
    category: "Accounting records",
    retention: "As required under applicable law",
    note: "Ledgers, vouchers, and financial statements",
  },
  {
    category: "Tax records",
    retention: "As required under applicable tax laws",
    note: "Income Tax Act & GST statutory retention periods",
  },
  {
    category: "Invoices & payment records",
    retention: "As required under applicable financial/tax laws",
    note: "GST tax invoices, payment receipts, and billing registers",
  },
  {
    category: "Compliance records",
    retention: "As required by applicable law",
    note: "ROC filings, secretarial registers, and annual return records",
  },
  {
    category: "User account information",
    retention: "During account period + required retention period",
    note: "Client portal profile, access history, and verification records",
  },
  {
    category: "Security logs",
    retention: "For security and investigation requirements",
    note: "Authentication logs, IP traces, and administrative audit trails",
  },
  {
    category: "Marketing preferences",
    retention: "Until withdrawn or no longer required",
    note: "Email newsletter consents and communication opt-ins",
  },
];

export default function DataRetentionPolicy() {
  const [activeSection, setActiveSection] = useState("scope");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 140;
      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const navOffset = 100;
      const top = el.getBoundingClientRect().top + window.pageYOffset - navOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="relative min-h-screen bg-slate-50 font-sans text-navy-900 antialiased" data-testid="data-retention-page">
      <SEO
        title="Data Retention & Deletion Policy | Biz360 Compliance"
        description="Official Data Retention and Deletion Policy for Biz360 Compliance. Explaining record retention categories, statutory timelines, client deletion requests, and secure disposal protocols."
        canonical="/data-retention-policy"
        breadcrumbs={[{ label: "Data Retention & Deletion Policy", href: "/data-retention-policy" }]}
      />
      <ScrollProgress />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-14 md:pt-40 md:pb-16 border-b border-slate-200">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-5xl px-5 lg:px-8">
            <Reveal>
              <div className="mb-6 flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-500" data-testid="data-retention-breadcrumb">
                <Link to="/" className="hover:text-navy-800 transition-colors">Home</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-navy-700">Data Retention & Deletion Policy</span>
              </div>
            </Reveal>

            <div className="text-center">
              <Reveal delay={0.05}>
                <span className="inline-flex items-center rounded-full border border-gold-300/60 bg-gold-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-gold-700">
                  Corporate Governance & Compliance
                </span>
              </Reveal>

              <Reveal delay={0.1}>
                <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-900 md:text-5xl">
                  DATA RETENTION & DELETION POLICY
                </h1>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white/80 px-4 py-1.5 text-xs font-medium text-slate-600 shadow-sm border border-slate-200">
                  <History className="h-3.5 w-3.5 text-gold-600" />
                  <span>Last Updated: <strong className="text-navy-900">01/09/2026</strong></span>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-navy-900/10 bg-white/90 p-6 text-left shadow-sm backdrop-blur-sm">
                  <p className="text-base font-semibold text-navy-900">
                    Biz360 Compliance follows a controlled approach to retaining and deleting personal, financial, business and service-related information.
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    The objective is to retain information for as long as reasonably necessary and not retain personal information indefinitely without a legitimate reason.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Content Container */}
        <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            
            {/* Quick Navigation Sticky Sidebar */}
            <aside className="hidden lg:col-span-4 lg:block">
              <div className="sticky top-28 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm max-h-[calc(100vh-140px)] overflow-y-auto">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 px-2">
                  Table of Contents
                </h3>
                <nav className="space-y-1">
                  {SECTIONS.map((sec) => {
                    const SIcon = sec.icon;
                    const isActive = activeSection === sec.id;
                    return (
                      <button
                        key={sec.id}
                        onClick={() => scrollToSection(sec.id)}
                        className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-xs font-medium transition-all ${
                          isActive
                            ? "bg-navy-900 text-white shadow-sm"
                            : "text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                        }`}
                      >
                        <SIcon className={`h-4 w-4 shrink-0 ${isActive ? "text-gold-400" : "text-slate-400"}`} />
                        <span className="truncate">{sec.title}</span>
                      </button>
                    );
                  })}
                </nav>

                <div className="mt-6 border-t border-slate-100 pt-5">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 px-2">
                    Related Policies
                  </h4>
                  <div className="space-y-1.5 text-xs font-medium">
                    <Link
                      to="/terms"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Terms & Conditions</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/privacy-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Privacy Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/data-security-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Data Security Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Policy Content */}
            <div className="space-y-8 lg:col-span-8">

              {/* 1. Scope */}
              <article id="scope" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Layers className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">1. Scope</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  This Policy applies to information maintained through:
                </p>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Website;",
                    "CRM;",
                    "Client portal;",
                    "Email and communication systems;",
                    "Accounting and billing systems;",
                    "Service records;",
                    "Documents;",
                    "Financial records; and",
                    "Other authorised business systems."
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2.5 rounded-lg bg-slate-50 p-2.5 border border-slate-100">
                      <span className="h-2 w-2 rounded-full bg-gold-500 shrink-0" />
                      <span className="text-xs font-semibold text-slate-800">{item}</span>
                    </div>
                  ))}
                </div>
              </article>

              {/* 2. Why We Retain Information */}
              <article id="why-retain" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <FolderClock className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">2. Why We Retain Information</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Information may be retained for:
                </p>
                <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Providing services;",
                    "Maintaining client records;",
                    "Accounting and billing;",
                    "Statutory and regulatory requirements;",
                    "Professional record-keeping;",
                    "Tax and financial records;",
                    "Dispute resolution;",
                    "Security and fraud prevention;",
                    "Audit trails; and",
                    "Other lawful business purposes."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>

              {/* 3. Retention Principles */}
              <article id="principles" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <CheckCircle2 className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">3. Retention Principles</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  We seek to ensure that information is:
                </p>
                <div className="mt-4 space-y-2.5 text-sm text-slate-700">
                  {[
                    "Retained only as long as necessary;",
                    "Accessible only to authorised persons;",
                    "Reviewed periodically;",
                    "Deleted or securely disposed of when no longer required; and",
                    "Retained longer where required by applicable law or a legitimate legal requirement."
                  ].map((item, idx) => (
                    <div key={item} className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50/70 p-3.5">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-navy-900 text-[10px] font-bold text-white">
                        {idx + 1}
                      </span>
                      <span className="text-xs font-semibold text-slate-800 leading-normal">{item}</span>
                    </div>
                  ))}
                </div>
              </article>

              {/* 4. Indicative Retention Categories */}
              <article id="categories" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <CalendarClock className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">4. Indicative Retention Categories</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Actual retention periods may vary according to the service and applicable law.
                </p>

                {/* Retention Schedule Table */}
                <div className="mt-5 overflow-hidden rounded-xl border border-slate-200">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-navy-900 text-white font-bold uppercase tracking-wider">
                        <tr>
                          <th className="p-3.5 sm:w-1/3">Information</th>
                          <th className="p-3.5">Indicative Retention</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {RETENTION_CATEGORIES.map((row, idx) => (
                          <tr key={row.category} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/60"}>
                            <td className="p-3.5 font-bold text-navy-900 align-top">
                              {row.category}
                            </td>
                            <td className="p-3.5 text-slate-700">
                              <span className="font-semibold text-slate-900">{row.retention}</span>
                              <span className="mt-0.5 block text-[11px] text-slate-500">{row.note}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-4 rounded-xl border border-gold-200 bg-gold-50/50 p-4">
                  <p className="text-xs font-semibold text-gold-900 leading-relaxed">
                    <strong>Statutory Precedence:</strong> The applicable statutory retention period will prevail where it is longer than the standard internal retention period.
                  </p>
                </div>
              </article>

              {/* 5. Deletion */}
              <article id="deletion" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Trash2 className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">5. Deletion</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  When information is no longer required and no legal, contractual or security reason exists for retaining it, Biz360 may:
                </p>
                <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Delete it;",
                    "Anonymise it;",
                    "Securely archive it where legally required; or",
                    "Render it inaccessible for ordinary operational use."
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <span className="h-2 w-2 rounded-full bg-rose-500 shrink-0" />
                      <span className="text-xs font-semibold text-slate-800">{item}</span>
                    </div>
                  ))}
                </div>
              </article>

              {/* 6. Account Closure */}
              <article id="account-closure" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <UserX className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">6. Account Closure</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  When a client account is closed, relevant information may continue to be retained where necessary for:
                </p>
                <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Outstanding services;",
                    "Billing;",
                    "Tax/accounting records;",
                    "Compliance records;",
                    "Dispute resolution;",
                    "Legal requirements; or",
                    "Security purposes."
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/50 p-3.5">
                  <p className="text-xs font-semibold text-amber-950">
                    <strong>Notice:</strong> Closing an account does not necessarily result in immediate deletion of all information.
                  </p>
                </div>
              </article>

              {/* 7. Client Deletion Requests */}
              <article id="client-requests" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <UserMinus className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">7. Client Deletion Requests</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Clients may request deletion of information where legally applicable.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-700 bg-slate-50 p-4 rounded-xl border border-slate-200">
                  A deletion request may not be accepted in full where retention is required or permitted by law, contract, professional record-keeping obligations, dispute resolution or other legitimate purposes.
                </p>
              </article>

              {/* 8. Backup Copies */}
              <article id="backups" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ArchiveRestore className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">8. Backup Copies</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Information deleted from active systems may continue to exist temporarily in secure backups until the relevant backup cycle expires.
                </p>
                <p className="mt-3 text-xs font-semibold text-slate-600 bg-slate-100 p-3 rounded-xl">
                  Backup information is not ordinarily used for routine business operations.
                </p>
              </article>

              {/* 9. Secure Disposal */}
              <article id="secure-disposal" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ShieldAlert className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">9. Secure Disposal</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  When information is due for disposal, reasonable measures may be used to prevent unauthorised recovery or access.
                </p>
              </article>

              {/* 10. Policy Review */}
              <article id="policy-review" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <RefreshCw className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">10. Policy Review</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Retention periods may be reviewed periodically based on:
                </p>
                <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Changes in law;",
                    "Regulatory requirements;",
                    "Business requirements;",
                    "Security considerations; and",
                    "Changes in technology or service architecture."
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2.5 rounded-lg bg-slate-50 p-2.5 border border-slate-100">
                      <span className="h-2 w-2 rounded-full bg-emerald-600 shrink-0" />
                      <span className="text-xs font-semibold text-slate-800">{item}</span>
                    </div>
                  ))}
                </div>
              </article>

            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
